import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, IconButton, Modal, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { EInteractionMode } from '../../@types/Enums';
import { IconAddAttribute, IconAddImage, IconAddPanorama, IconAddPdf } from '../../assets/icons';
import { AttachmentAsset } from '../../models/standard/AttachmentAsset';
import AttachmentModel, { AttachmentType } from '../../models/standard/AttachmentModel';
import AttributeModel from '../../models/standard/AttributeModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useLoginContext } from '../../provider/LoginProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import AttachmentAttributeModal from './AttachmentAttributeModal';
import AttachmentImageModal from './AttachmentImageModal';
import AttachmentPanoramaModal from './AttachmentPanoramaModal';
import AttachmentPdfModal from './AttachmentPdfModal';

const useStyles = makeStyles((theme) => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(1.5, 2),
    paddingTop: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    border: 'none',
  },
  dividerFull: {
    marginLeft: '-16px !important',
    marginRight: '-16px !important',
    backgroundColor: '#d3d3d3',
    height: '1px',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  modalTitle: {
    fontSize: '1.3rem !important',
    fontWeight: 500,
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    gap: 0,
  },
  tileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: 130,
    marginBottom: 6,
  },
  tile: {
    width: 100,
    height: 100,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: '0.2s',
    '&:hover': {
      backgroundColor: '#f4f4f4',
    },
    '&:hover svg path': {
      fill: '#464646 !important',
    },
  },
  tileDisabled: {
    opacity: 0.4,
    pointerEvents: 'none',
    cursor: 'default',
    backgroundColor: '#f9f9f9',
    '& svg path': {
      fill: '#d3d3d3 !important',
    },
  },
  icon: {
    width: '100px',
    height: '100px',
    '& path': {
      fill: '#A3A3A3 !important',
    },
  },
  tileLabel: {
    fontSize: '1.2rem',
    color: '#333',
    marginTop: theme.spacing(0.5),
  },
  tileLabelDisabled: {
    color: '#999',
  },
  closeIcon: {
    fontSize: '1.4rem !important',
  },
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const AttachmentModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { setPendingAttachment } = useAnnotations();
  const { isAuthenticated } = useLoginContext();
  const { setInteractionMode } = useToolbarContext();
  const { planModel } = usePlan();

  const [isPanoramaModalOpen, setPanoramaModalOpen] = useState(false);
  const [isAttributeModalOpen, setAttributeModalOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isPdfModalOpen, setPdfModalOpen] = useState(false);

  const hasProjectIds = planModel && planModel.kojeId !== -1 && planModel.eventId !== -1;
  const hasKojeLevelIds = planModel && planModel.kojeId !== -1 && planModel.kojeLevel !== -1;

  const isMediaDisabled = !(hasProjectIds || hasKojeLevelIds);

  const handleSelect = (type: string) => {
    if (isMediaDisabled && (type === 'panorama' || type === 'image' || type === 'pdf')) {
      return;
    }

    if (type === 'attribute' && !isAuthenticated) {
      return;
    }

    onClose();

    if (type === 'panorama') {
      setPanoramaModalOpen(true);
      return;
    }
    if (type === 'attribute') {
      setAttributeModalOpen(true);
      return;
    }
    if (type === 'image') {
      setImageModalOpen(true);
      return;
    }
    if (type === 'pdf') {
      setPdfModalOpen(true);
      return;
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}>
        <Box className={classes.modalBox}>
          <div className={classes.modalHeader}>
            <Typography className={classes.modalTitle} style={{ color: '#464646' }}>
              {formatMessage({ id: 'NewAttachmentModal.title', defaultMessage: 'Neues Attachment erstellen' })}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon className={classes.closeIcon} style={{ color: '#464646' }} />
            </IconButton>
          </div>

          <Divider className={classes.dividerFull} />

          <div className={classes.optionsRow}>
            <div className={classes.tileWrapper}>
              <Box className={`${classes.tile} ${isMediaDisabled ? classes.tileDisabled : ''}`} onClick={() => handleSelect('panorama')}>
                <IconAddPanorama className={classes.icon} />
              </Box>
              <Typography className={isMediaDisabled ? classes.tileLabelDisabled : classes.tileLabel}>Panorama</Typography>
            </div>

            <div className={classes.tileWrapper}>
              <Box className={`${classes.tile} ${isMediaDisabled ? classes.tileDisabled : ''}`} onClick={() => handleSelect('image')}>
                <IconAddImage className={classes.icon} />
              </Box>
              <Typography className={isMediaDisabled ? classes.tileLabelDisabled : classes.tileLabel}>Bild</Typography>
            </div>

            <div className={classes.tileWrapper}>
              <Box className={`${classes.tile} ${isMediaDisabled ? classes.tileDisabled : ''}`} onClick={() => handleSelect('pdf')}>
                <IconAddPdf className={classes.icon} />
              </Box>
              <Typography className={isMediaDisabled ? classes.tileLabelDisabled : classes.tileLabel}>PDF</Typography>
            </div>

            <div className={classes.tileWrapper}>
              <Box className={`${classes.tile} ${!isAuthenticated ? classes.tileDisabled : ''}`} onClick={() => handleSelect('attribute')}>
                <IconAddAttribute className={classes.icon} />
              </Box>
              <Typography className={!isAuthenticated ? classes.tileLabelDisabled : classes.tileLabel}>Attribut</Typography>
            </div>
          </div>
        </Box>
      </Modal>

      {isPanoramaModalOpen && (
        <AttachmentPanoramaModal
          isOpen={isPanoramaModalOpen}
          onClose={() => setPanoramaModalOpen(false)}
          onSelect={(asset: AttachmentAsset) => {
            const attachment = new AttachmentModel(AttachmentType.PANORAMA, asset);
            setPendingAttachment(attachment);
            setPanoramaModalOpen(false);
            setInteractionMode(EInteractionMode.ATTACHMENT);
          }}
        />
      )}

      {isAttributeModalOpen && isAuthenticated && (
        <AttachmentAttributeModal
          isOpen={isAttributeModalOpen}
          onClose={() => setAttributeModalOpen(false)}
          onSelect={(attribute: AttributeModel) => {
            const attachment = new AttachmentModel(AttachmentType.ATTRIBUTE, attribute);
            setPendingAttachment(attachment);
            setAttributeModalOpen(false);
            setInteractionMode(EInteractionMode.ATTACHMENT);
          }}
        />
      )}

      {isImageModalOpen && (
        <AttachmentImageModal
          isOpen={isImageModalOpen}
          onClose={() => setImageModalOpen(false)}
          onSelect={(asset: AttachmentAsset) => {
            const attachment = new AttachmentModel(AttachmentType.IMAGE, asset);
            setPendingAttachment(attachment);
            setImageModalOpen(false);
            setInteractionMode(EInteractionMode.ATTACHMENT);
          }}
        />
      )}

      {isPdfModalOpen && (
        <AttachmentPdfModal
          isOpen={isPdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          onSelect={(asset: AttachmentAsset) => {
            const attachment = new AttachmentModel(AttachmentType.PDF, asset);
            setPendingAttachment(attachment);
            setPdfModalOpen(false);
            setInteractionMode(EInteractionMode.ATTACHMENT);
          }}
        />
      )}
    </>
  );
};

export default AttachmentModal;
